import { useEffect, useMemo, useRef, useState } from "react";
import "./propiedades.css";
import {
  Alert,
  Button,
  Collapse,
  Dropdown,
  Form,
  Grid,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  SettingOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Propiedad,
  PropiedadUpsert,
  usePropiedadesLogic,
  apiToUi,
} from "./usePropiedadesLogic";
import { useSearchParams } from "react-router-dom";
import { propiedadesApi } from "../../services/propiedades.api";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { API_BASE_URL } from "../../services/apiClient";
import { getModal } from "../../utils/antdStatic";
import { conIvaFromNeto, netoFromConIva } from "../../utils/iva";
import BodeguitasTab from "./BodeguitasTab";
import BodegasTab from "./BodegasTab";
import CreateUserModal from "./components/CreateUserModal";
import PropiedadLocationPanel from "./components/PropiedadLocationPanel";
import PropiedadOwnershipPanel from "./components/PropiedadOwnershipPanel";
import {
  PropiedadIdentityPanel,
  PropiedadGuestPanel,
} from "./components/PropiedadSimplePanels";
import PropiedadFinancePanel from "./components/PropiedadFinancePanel";
import PropiedadIntegrationPanel from "./components/PropiedadIntegrationPanel";
import PropiedadesColumnSelector from "./components/PropiedadesColumnSelector";
import EliminarPropiedadModal from "./components/EliminarPropiedadModal";
import { configOptionApi } from "../../services/configOption.api";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { UserCell } from "../../Components/UserAvatar";

const { Text } = Typography;

const renderMappedName = (
  id: number | null | undefined,
  map: Map<number, string>,
  catalogsLoading: boolean,
) => {
  if (!id) return "-";
  if (catalogsLoading && map.size === 0) return <Spin size="small" />;
  const name = map.get(id);
  return name ?? <Text type="secondary">ID: {id}</Text>;
};

type ColKey =
  | "nombrePropiedad"
  | "nombrePublicacion"
  | "plan"
  | "comisionPropiedad"
  | "precioMinimoNoche"
  | "precioLimpiezaCobrado"
  | "precioLimpiezaAseador"
  | "region"
  | "comuna"
  | "sector"
  | "pais"
  | "idPropietario"
  | "idCliente"
  | "idPropiedadCorto"
  | "idPropiedadLargo"
  | "calle"
  | "numeroCalle"
  | "casaODepto"
  | "torreOConjunto"
  | "piso"
  | "numeroDeptoCasa"
  | "nombreCondominio"
  | "estacionamiento"
  | "otros"
  | "claveWifi"
  | "habilitado"
  | "claveCerradura"
  | "lavanderiaId"
  | "acciones";

const COL_LABELS: Record<ColKey, string> = {
  nombrePropiedad: "Nombre propiedad",
  nombrePublicacion: "Nombre publicación",
  plan: "Plan",
  comisionPropiedad: "Comisión (%)",
  precioMinimoNoche: "Mín. noche acordado",
  precioLimpiezaCobrado: "Limpieza cobrada",
  precioLimpiezaAseador: "Pago aseador",
  region: "Región (ID)",
  comuna: "Comuna (ID)",
  sector: "Sector (ID)",
  pais: "País (ID)",
  idPropietario: "Propietario(s)",
  idCliente: "Cliente (ID)",
  idPropiedadCorto: "ID corto",
  idPropiedadLargo: "ID largo",
  calle: "Calle",
  numeroCalle: "N°",
  casaODepto: "Casa/Depto",
  torreOConjunto: "Torre/Conjunto",
  piso: "Piso",
  numeroDeptoCasa: "Depto/Casa",
  nombreCondominio: "Condominio",
  estacionamiento: "Estacionamiento",
  otros: "Otros",
  habilitado: "Habilitada",
  claveWifi: "WiFi",
  claveCerradura: "Clave cerradura",
  lavanderiaId: "Lavandería",
  acciones: "Acciones",
};

function money(v?: number | null) {
  if (v === null || v === undefined) return "-";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(v);
}

export default function Propiedades() {
  const {
    limit,
    setLimit,
    items,
    loading,
    catalogsLoading,
    error,
    page,
    total,
    setPage,
    visibleCols,
    setVisibleCols,
    defaultVisible,
    createPropiedad,
    updatePropiedad,
    deletePropiedad,
    toggleHabilitado,
    sidebarOpen,
    toggleSidebar,
    paisIds,
    setPaisIds,
    regionIds,
    setRegionIds,
    comunaIds,
    setComunaIds,
    sectorIds,
    setSectorIds,
    ownerSearch,
    setOwnerSearch,
    ownerType,
    setOwnerType,
    qProp,
    setQProp,
    clearGeoFilters,
    paisOptions,
    regionOptions,
    comunaOptions,
    sectorOptions,
    setSectorOptions,
    lavanderiaOptions,
    filteredRegionOptions,
    filteredComunaOptions,
    users,
    allPropertyNames,
    createUsuario,
  } = usePropiedadesLogic();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [form] = Form.useForm<PropiedadUpsert>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Pestaña activa (Propiedades / Bodeguitas / Bodegas). El enlace directo
  // `/propiedades?edit=<id>` abre el modal de UNA propiedad: ahí manda la URL
  // y se aterriza en "Propiedades", aunque la última vez se hubiera quedado
  // en otra pestaña.
  const hayEnlaceDirecto = Boolean(searchParams.get("edit"));
  const [pestana, setPestana] = useFiltroPersistido<string>(
    "propiedades.pestana",
    "propiedades",
    {
      validar: (v) =>
        v === "propiedades" || v === "bodeguitas" || v === "bodegas",
      restaurar: !hayEnlaceDirecto,
    },
  );
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<Propiedad | null>(null);
  // Propiedad cuyo borrado se está revisando. El modal consulta el impacto real
  // antes de dejar confirmar (round 2026-08-06).
  const [borrarTarget, setBorrarTarget] = useState<{
    id: number;
    nombre: string;
  } | null>(null);
  // El panel de filtros avanzados no se guarda, pero se abre solo si al entrar
  // ya hay un filtro geográfico recordado: si no, la tabla saldría recortada
  // por unos selects que quedan escondidos y no se pueden ni ver ni limpiar.
  const [showAdvanced, setShowAdvanced] = useState(
    () =>
      paisIds.length > 0 ||
      regionIds.length > 0 ||
      comunaIds.length > 0 ||
      sectorIds.length > 0,
  );
  const [buscandoIdLargo, setBuscandoIdLargo] = useState(false);
  // El ID largo es derivado — se sincroniza desde Stays vía el botón
  // "Obtener ID largo" al lado del ID corto. Queda read-only por default
  // para dejar clara la dirección del flujo (corto → largo). El admin
  // puede destrabarlo con el candado para casos de edge (ID pegado a mano).
  const [idLargoUnlocked, setIdLargoUnlocked] = useState(false);
  const [openUserModal, setOpenUserModal] = useState(false);
  const [userCreateTarget, setUserCreateTarget] = useState<"owner" | "client">(
    "owner",
  );
  const [planOptions, setPlanOptions] = useState<string[]>([]);
  const planSelectRef = useRef<any>(null);
  const sectorSelectRef = useRef<any>(null);
  const [creandoSector, setCreandoSector] = useState(false);
  // ─────────────────────────────────────────────────────────────
  // Cascada geo del modal crear/editar:
  //   país → región → comuna → sector
  // Cada Select filtra sus opciones según el padre seleccionado, y
  // al cambiar el padre se limpian los hijos para evitar estados
  // inconsistentes (ej. dejar una comuna de otra región).
  // ─────────────────────────────────────────────────────────────
  const watchPaisModal = Form.useWatch("pais", form);
  const watchRegionModal = Form.useWatch("region", form);
  const watchComunaModal = Form.useWatch("comuna", form);

  // ─────────────────────────────────────────────────────────────
  // Desglose de limpieza — auto-cálculo simple: neto ↔ total, IVA derivado.
  //
  // Cada subitem con IVA (transporte, materiales, lavandería) tiene 3 campos:
  //   neto, iva, total  con  iva = round(neto * 0.19)  y  total = neto + iva.
  //
  // Decisión UX: el IVA se muestra SIEMPRE read-only. Los únicos campos
  // editables son neto y total; al tocar cualquiera, los otros dos se
  // derivan automáticamente vía onChange del InputNumber correspondiente.
  //
  //   neto  → iva = round(neto * 0.19); total = neto + iva
  //   total → neto = round(total / 1.19); iva = total - neto
  //
  // No usamos useEffect con watch porque eso re-dispara al escribir
  // setFieldValue y puede pelear contra el typing del usuario en
  // InputNumbers. onChange del campo tocado es autoritativo.
  //
  // Además:
  //   - pago_aseador: 1 sólo campo, sin IVA (el aseador es persona natural).
  //   - total_limpieza_cobrado = pago_aseador + transporte.total
  //                              + materiales.total + lavandería.total
  //     (monto cobrado al huésped, INCLUYE IVA). Siempre derivado.
  //
  // Ver docs/modulo-arriendos-precios.md §Desglose de limpieza.
  // ─────────────────────────────────────────────────────────────
  const watchAseador = Form.useWatch("precioLimpiezaAseador", form) ?? 0;

  const watchTransporteNeto = Form.useWatch("precioTransporteNeto", form) ?? 0;
  const watchTransporteIva = Form.useWatch("precioTransporteIva", form) ?? 0;
  const watchTransporteTotal = Form.useWatch("precioTransporteTotal", form) ?? 0;

  const watchMaterialesNeto = Form.useWatch("precioMaterialesNeto", form) ?? 0;
  const watchMaterialesIva = Form.useWatch("precioMaterialesIva", form) ?? 0;
  const watchMaterialesTotal = Form.useWatch("precioMaterialesTotal", form) ?? 0;

  const watchLavanderiaNeto = Form.useWatch("precioLavanderiaNeto", form) ?? 0;
  const watchLavanderiaIva = Form.useWatch("precioLavanderiaIva", form) ?? 0;
  const watchLavanderiaTotal = Form.useWatch("precioLavanderiaTotal", form) ?? 0;

  type SubitemPrefix = "Transporte" | "Materiales" | "Lavanderia";

  // Usuario editó el neto → derivar iva y total. IVA centralizado en utils/iva.ts.
  const handleNetoChange = (prefix: SubitemPrefix, rawValue: unknown) => {
    const neto = Math.max(0, Math.round(Number(rawValue) || 0));
    const total = conIvaFromNeto(neto);
    const iva = total - neto;
    form.setFieldsValue({
      [`precio${prefix}Neto`]: neto,
      [`precio${prefix}Iva`]: iva,
      [`precio${prefix}Total`]: total,
    });
  };

  // Usuario editó el total → derivar neto e iva.
  const handleTotalChange = (prefix: SubitemPrefix, rawValue: unknown) => {
    const total = Math.max(0, Math.round(Number(rawValue) || 0));
    const neto = netoFromConIva(total);
    const iva = total - neto;
    form.setFieldsValue({
      [`precio${prefix}Neto`]: neto,
      [`precio${prefix}Iva`]: iva,
      [`precio${prefix}Total`]: total,
    });
  };

  // ─── Auto-derivación defensiva de IVA/Total desde Neto ──────────
  // Si al cargar una propiedad legacy el IVA quedó en 0 pero el Neto
  // es > 0 (el bootstrap copió `precio_materiales → neto = total, iva = 0`
  // sin recalcular el 19 %), el modal mostraría un IVA incorrecto y,
  // hasta que el usuario no tocara Neto/Total, no se corregiría.
  //
  // Este efecto patch-ea ese caso: si vemos `iva === 0 && neto > 0`,
  // asumimos que el Neto es autoritativo (primer caso de migración,
  // el más común) y derivamos IVA = round(neto * 0.19) y Total = neto + IVA.
  //
  // Si el usuario después edita Total y el Neto se recalcula (vía
  // `handleTotalChange`), el IVA resultante será `total - neto ≠ 0`,
  // entonces la condición deja de disparar y no hay loops.
  useEffect(() => {
    const netoNum = Number(watchTransporteNeto) || 0;
    const ivaNum = Number(watchTransporteIva) || 0;
    if (netoNum > 0 && ivaNum === 0) {
      const iva = conIvaFromNeto(netoNum) - netoNum;
      form.setFieldsValue({
        precioTransporteIva: iva,
        precioTransporteTotal: netoNum + iva,
      });
    }
  }, [watchTransporteNeto, watchTransporteIva, form]);

  useEffect(() => {
    const netoNum = Number(watchMaterialesNeto) || 0;
    const ivaNum = Number(watchMaterialesIva) || 0;
    if (netoNum > 0 && ivaNum === 0) {
      const iva = conIvaFromNeto(netoNum) - netoNum;
      form.setFieldsValue({
        precioMaterialesIva: iva,
        precioMaterialesTotal: netoNum + iva,
      });
    }
  }, [watchMaterialesNeto, watchMaterialesIva, form]);

  useEffect(() => {
    const netoNum = Number(watchLavanderiaNeto) || 0;
    const ivaNum = Number(watchLavanderiaIva) || 0;
    if (netoNum > 0 && ivaNum === 0) {
      const iva = conIvaFromNeto(netoNum) - netoNum;
      form.setFieldsValue({
        precioLavanderiaIva: iva,
        precioLavanderiaTotal: netoNum + iva,
      });
    }
  }, [watchLavanderiaNeto, watchLavanderiaIva, form]);

  // Total limpieza cobrado al huésped — SIEMPRE derivado.
  // "Por el total obvio": la casilla sin grado de libertad es ésta.
  useEffect(() => {
    const cobrado =
      Number(watchAseador) +
      Number(watchTransporteTotal) +
      Number(watchMaterialesTotal) +
      Number(watchLavanderiaTotal);
    form.setFieldValue("precioLimpiezaCobrado", cobrado);
  }, [
    watchAseador,
    watchTransporteTotal,
    watchMaterialesTotal,
    watchLavanderiaTotal,
    form,
  ]);

  // ─── Verificador de cuentas ────────────────────────────────────
  // Calcula los deltas para detectar inconsistencias. Con los useEffect
  // de arriba, cualquier edición del usuario converge. El único caso
  // realista de delta ≠ 0 es carga desde DB con valores legacy
  // inconsistentes (pre-migración de subitems) — el alert lo flaggea
  // para que el admin guarde con los nuevos valores coherentes.
  const deltaTransporte = Math.abs(
    Number(watchTransporteTotal) -
      (Number(watchTransporteNeto) + Number(watchTransporteIva)),
  );
  const deltaMateriales = Math.abs(
    Number(watchMaterialesTotal) -
      (Number(watchMaterialesNeto) + Number(watchMaterialesIva)),
  );
  const deltaLavanderia = Math.abs(
    Number(watchLavanderiaTotal) -
      (Number(watchLavanderiaNeto) + Number(watchLavanderiaIva)),
  );
  const sumaSubitems =
    Number(watchAseador) +
    Number(watchTransporteTotal) +
    Number(watchMaterialesTotal) +
    Number(watchLavanderiaTotal);
  const deltasSubitems = [deltaTransporte, deltaMateriales, deltaLavanderia];
  const hasDeltaSubitems = deltasSubitems.some((d) => d > 1);

  useEffect(() => {
    const fetchPlanOptions = async () => {
      const res = await configOptionApi.list("plan");
      setPlanOptions(res?.data?.map((o) => o.valor) ?? []);
    };
    fetchPlanOptions();
  }, []);

  const usersById = useMemo(
    () =>
      new Map(
        users.map((u) => [
          u.id,
          u.rol === "cliente" ? u.nombre_usuario : u.nombre_propietario,
        ]),
      ),
    [users],
  );

  // Avatar por id → carita del cliente-gestor en la columna Cliente. Solo los
  // clientes (rol con login) traen avatar; los propietarios no (y no llevan
  // carita, así que este map no se usa en la columna Propietario).
  const clienteAvatarById = useMemo(
    () =>
      new Map<number, { avatar?: string | null; avatarUrl?: string | null }>(
        users.map((u) => [
          u.id,
          { avatar: u.avatar ?? null, avatarUrl: u.avatar_url ?? null },
        ]),
      ),
    [users],
  );

  const paisById = useMemo(
    () => new Map(paisOptions.map((x) => [x.id, x.pais])),
    [paisOptions],
  );

  const regionById = useMemo(
    () => new Map(regionOptions.map((x) => [x.id, x.region])),
    [regionOptions],
  );

  const comunaById = useMemo(
    () => new Map(comunaOptions.map((x) => [x.id, x.comuna])),
    [comunaOptions],
  );

  const sectorById = useMemo(
    () => new Map(sectorOptions.map((x) => [x.id, x.sector])),
    [sectorOptions],
  );

  const lavanderiaById = useMemo(
    () => new Map(lavanderiaOptions.map((x) => [x.id, x.nombre])),
    [lavanderiaOptions],
  );

  // ─── Opciones geo filtradas dentro del modal ─────────────────────
  // Si no hay padre seleccionado, mostramos todas las opciones (el
  // usuario puede cargar "desde abajo hacia arriba" — al elegir una
  // comuna, el padre se puede inferir en el futuro). Cuando el padre
  // existe, filtramos estrictamente para evitar estados inválidos.
  const modalRegionOptions = useMemo(() => {
    if (watchPaisModal == null) return regionOptions;
    return regionOptions.filter((r) => r.pais === watchPaisModal);
  }, [regionOptions, watchPaisModal]);

  const modalComunaOptions = useMemo(() => {
    if (watchRegionModal == null) return comunaOptions;
    return comunaOptions.filter((c) => c.region === watchRegionModal);
  }, [comunaOptions, watchRegionModal]);

  const modalSectorOptions = useMemo(() => {
    if (watchComunaModal == null) return sectorOptions;
    return sectorOptions.filter((s) => s.comuna === watchComunaModal);
  }, [sectorOptions, watchComunaModal]);

  // Opciones AntD-shape memoizadas para los Selects de pais/region/comuna/sector.
  // Antes se hacian `.map(...)` inline en JSX, creando arrays nuevos en cada
  // render y forzando re-render de los Select.
  const paisSelectOptions = useMemo(
    () => paisOptions.map((p) => ({ value: p.id, label: p.pais })),
    [paisOptions],
  );
  const regionSelectOptions = useMemo(
    () => modalRegionOptions.map((r) => ({ value: r.id, label: r.region })),
    [modalRegionOptions],
  );
  const comunaSelectOptions = useMemo(
    () => modalComunaOptions.map((c) => ({ value: c.id, label: c.comuna })),
    [modalComunaOptions],
  );
  const sectorSelectOptions = useMemo(
    () => modalSectorOptions.map((s) => ({ value: s.id, label: s.sector })),
    [modalSectorOptions],
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    // Default obligatorio: la lavandería genérica (es_generica=true) es la
    // opción por defecto cuando una propiedad no tiene AF interna asignada.
    // Se pre-selecciona acá para que el admin no tenga que elegirla a mano:
    // basta con guardar y la propiedad queda apuntando a "Lavandería externa".
    // Si la genérica no está cargada todavía (race con fetch), el backend la
    // defaultea igual al crear — pero queda más prolijo si el form ya la muestra.
    const genericaId = lavanderiaOptions.find((l) => l.es_generica)?.id ?? null;
    form.setFieldsValue({
      nombrePropiedad: "",
      plan: "Estandar" as any,
      comisionPropiedad: 0 as any,
      lavanderiaId: genericaId,
    });
    setIdLargoUnlocked(false);
    setOpenModal(true);
  };

  const openEdit = (p: Propiedad) => {
    setEditing(p);
    form.setFieldsValue({ ...p } as any);
    setIdLargoUnlocked(false);
    setOpenModal(true);
  };

  // Deep-link `/propiedades?edit=<id>`: abre directo el modal de configuración
  // de esa propiedad. Lo usan los links de propiedad en /clientes-propietarios.
  // Traemos la propiedad por id (getById) en vez de buscarla en `items`, porque
  // la lista es paginada server-side y la propiedad objetivo puede no estar en
  // la página actual. Corre una sola vez al montar (ref guard, StrictMode-safe).
  const deeplinkRef = useRef(false);
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || deeplinkRef.current) return;
    deeplinkRef.current = true;
    const id = Number(editId);
    // Limpiamos el query param para que un refresh no re-abra el modal.
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
    if (!Number.isFinite(id)) return;
    (async () => {
      const res = await propiedadesApi.getById(id);
      if (res?.data) {
        // getById devuelve el modelo crudo (snake_case) → apiToUi lo mapea al
        // shape que el form de edición espera.
        openEdit(apiToUi(res.data as any));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => {
    setOpenModal(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (err: any) {
      // AntD ya muestra el asterisco rojo y el error inline bajo cada
      // campo faltante, pero en un form de 6 paneles + Select/InputNumber
      // es fácil perderse. Mostramos además un Modal con el listado
      // explícito de qué falta, para que el admin sepa a primera vista.
      const errorFields: { name: (string | number)[]; errors: string[] }[] =
        err?.errorFields ?? [];
      if (errorFields.length) {
        getModal()?.error({
          title: "Hay campos por corregir",
          width: 520,
          content: (
            <div>
              <p style={{ marginTop: 0 }}>
                Para {editing ? "editar" : "crear"} la propiedad, corrige lo
                siguiente:
              </p>
              <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                {errorFields.map((f, i) => (
                  <li key={i}>
                    <strong>
                      {COL_LABELS[String(f.name[0]) as ColKey] ??
                        String(f.name[0])}
                    </strong>
                    : {f.errors[0]}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
      }
      return;
    }

    // Normaliza strings vacíos -> null para campos opcionales
    const normalize = (obj: any) => {
      const out: any = { ...obj };
      Object.keys(out).forEach((k) => {
        if (out[k] === "") out[k] = null;
      });
      return out;
    };

    // Ant Design omite keys con valor undefined; forzar array vacío si se limpió
    if (!Array.isArray(values.propietarioIds)) values.propietarioIds = [];
    const payload = normalize(values) as PropiedadUpsert;

    const ok = editing
      ? await updatePropiedad(editing.id, payload)
      : await createPropiedad(payload);

    if (ok) closeModal();
  };

  const allColumns: ColumnsType<Propiedad> = useMemo(() => {
    const cols: ColumnsType<Propiedad> = [
      {
        key: "nombrePropiedad",
        dataIndex: "nombrePropiedad",
        title: "Propiedad",
        fixed: isMobile ? undefined : "left",
        width: 240,
        ellipsis: true,
        sorter: (a, b) =>
          (a.nombrePropiedad ?? "").localeCompare(b.nombrePropiedad ?? ""),
        render: (v: string) => formatPropertyName(v) || "-",
      },
      {
        key: "nombrePublicacion",
        dataIndex: "nombrePublicacion",
        title: "Publicación",
        width: 220,
        ellipsis: true,
        sorter: (a, b) =>
          (a.nombrePublicacion ?? "").localeCompare(b.nombrePublicacion ?? ""),
        render: (v) => v || <Text type="secondary">-</Text>,
      },
      {
        key: "habilitado",
        dataIndex: "habilitado",
        title: (
          <Space size={4}>
            Habilitada
            <Tooltip title="Si se deshabilita, el sistema no procesará estadías ni bloqueos de esta propiedad. Los clientes no podrán verla ni acceder a su información. Si es su única propiedad, no podrán iniciar sesión.">
              <InfoCircleOutlined style={{ color: "#999", cursor: "help" }} />
            </Tooltip>
          </Space>
        ),
        width: 130,
        align: "center",
        sorter: (a, b) => Number(a.habilitado) - Number(b.habilitado),
        showSorterTooltip: false,
        render: (v: boolean, record: Propiedad) => (
          <Switch
            checked={v !== false}
            size="small"
            onChange={(checked) => toggleHabilitado(record.id, checked)}
          />
        ),
      },
      {
        key: "plan",
        dataIndex: "plan",
        title: (
          <Space size={4}>
            Plan
            <Tooltip title="El plan es solo una etiqueta de referencia. NO fija la comisión: la comisión es independiente y puede no calzar con el plan (p.ej. rebajas de comisión).">
              <InfoCircleOutlined style={{ color: "#999", cursor: "help" }} />
            </Tooltip>
          </Space>
        ),
        width: 140,
        sorter: (a, b) => (a.plan ?? "").localeCompare(b.plan ?? ""),
        render: (v) => (v ? <Tag>{v}</Tag> : <Text type="secondary">-</Text>),
      },
      {
        key: "comisionPropiedad",
        dataIndex: "comisionPropiedad",
        title: "Comisión (%)",
        width: 140,
        align: "right",
        sorter: (a, b) =>
          (a.comisionPropiedad ?? 0) - (b.comisionPropiedad ?? 0),
        render: (v) =>
          v === null || v === undefined ? "-" : `${Number(v).toFixed(2)}%`,
      },
      {
        key: "precioMinimoNoche",
        dataIndex: "precioMinimoNoche",
        title: "Mín. noche acordado",
        width: 170,
        align: "right",
        sorter: (a, b) =>
          (a.precioMinimoNoche ?? 0) - (b.precioMinimoNoche ?? 0),
        render: (v) => money(v),
      },
      {
        key: "precioLimpiezaCobrado",
        dataIndex: "precioLimpiezaCobrado",
        title: "Limpieza cobrada",
        width: 160,
        align: "right",
        sorter: (a, b) =>
          (a.precioLimpiezaCobrado ?? 0) - (b.precioLimpiezaCobrado ?? 0),
        render: (v) => money(v),
      },
      {
        key: "precioLimpiezaAseador",
        dataIndex: "precioLimpiezaAseador",
        title: "Pago aseador",
        width: 150,
        align: "right",
        sorter: (a, b) =>
          (a.precioLimpiezaAseador ?? 0) - (b.precioLimpiezaAseador ?? 0),
        render: (v) => money(v),
      },

      // IDs de ubicación (útiles pero no siempre)
      {
        key: "pais",
        title: "País",
        dataIndex: "pais",
        width: 160,
        ellipsis: true,
        sorter: (a, b) =>
          (paisById.get(a.pais!) ?? "").localeCompare(
            paisById.get(b.pais!) ?? "",
          ),
        render: (id) => renderMappedName(id, paisById, catalogsLoading),
      },
      {
        key: "region",
        title: "Región",
        dataIndex: "region",
        width: 180,
        ellipsis: true,
        sorter: (a, b) =>
          (regionById.get(a.region!) ?? "").localeCompare(
            regionById.get(b.region!) ?? "",
          ),
        render: (id) => renderMappedName(id, regionById, catalogsLoading),
      },
      {
        key: "comuna",
        title: "Comuna",
        dataIndex: "comuna",
        width: 180,
        ellipsis: true,
        sorter: (a, b) =>
          (comunaById.get(a.comuna!) ?? "").localeCompare(
            comunaById.get(b.comuna!) ?? "",
          ),
        render: (id) => renderMappedName(id, comunaById, catalogsLoading),
      },
      {
        key: "sector",
        title: "Sector",
        dataIndex: "sector",
        width: 200,
        ellipsis: true,
        sorter: (a, b) =>
          (sectorById.get(a.sector!) ?? "").localeCompare(
            sectorById.get(b.sector!) ?? "",
          ),
        render: (id) => renderMappedName(id, sectorById, catalogsLoading),
      },

      // Dueños / clientes
      {
        key: "idPropietario",
        title: "Propietario",
        dataIndex: "propietarioIds",
        width: 200,
        ellipsis: true,
        sorter: (a, b) => {
          const nameA = (a.propietarioIds ?? [])
            .map((id) => usersById.get(id) ?? "")
            .join(", ");
          const nameB = (b.propietarioIds ?? [])
            .map((id) => usersById.get(id) ?? "")
            .join(", ");
          return nameA.localeCompare(nameB);
        },
        render: (ids: number[]) => {
          if (!Array.isArray(ids) || ids.length === 0) return "-";
          if (catalogsLoading && usersById.size === 0)
            return <Spin size="small" />;
          return ids.map((id) => usersById.get(id) ?? `ID: ${id}`).join(", ");
        },
      },
      {
        key: "idCliente",
        title: "Cliente",
        dataIndex: "idCliente",
        width: 220,
        ellipsis: true,
        sorter: (a, b) =>
          (usersById.get(a.idCliente!) ?? "").localeCompare(
            usersById.get(b.idCliente!) ?? "",
          ),
        // El cliente-gestor (rol con login) se muestra con su carita.
        render: (id: number | null | undefined) => {
          if (!id) return "-";
          if (catalogsLoading && usersById.size === 0)
            return <Spin size="small" />;
          const name = usersById.get(id);
          if (!name) return <Text type="secondary">ID: {id}</Text>;
          const av = clienteAvatarById.get(id);
          return (
            <UserCell
              nombre={name}
              avatarSeed={av?.avatar}
              avatarUrl={av?.avatarUrl}
              size={28}
            />
          );
        },
      },

      // IDs property
      {
        key: "idPropiedadCorto",
        dataIndex: "idPropiedadCorto",
        title: "ID propiedad corto",
        width: 140,
        ellipsis: true,
        sorter: (a, b) =>
          (a.idPropiedadCorto ?? "").localeCompare(b.idPropiedadCorto ?? ""),
        render: (v) => v || "-",
      },
      {
        key: "idPropiedadLargo",
        dataIndex: "idPropiedadLargo",
        title: "ID propiedad largo",
        width: 300,
        ellipsis: true,
        sorter: (a, b) =>
          (a.idPropiedadLargo ?? "").localeCompare(b.idPropiedadLargo ?? ""),
        render: (v) => v || "-",
      },

      // Dirección / detalle
      {
        key: "calle",
        dataIndex: "calle",
        title: "Calle",
        width: 200,
        ellipsis: true,
        sorter: (a, b) => (a.calle ?? "").localeCompare(b.calle ?? ""),
        render: (v) => v || "-",
      },
      {
        key: "numeroCalle",
        dataIndex: "numeroCalle",
        title: "N°",
        width: 80,
        align: "center",
        sorter: (a, b) => (a.numeroCalle ?? 0) - (b.numeroCalle ?? 0),
        render: (v) => v ?? "-",
      },
      {
        key: "casaODepto",
        dataIndex: "casaODepto",
        title: "Casa/Depto",
        width: 120,
        sorter: (a, b) =>
          (a.casaODepto ?? "").localeCompare(b.casaODepto ?? ""),
        render: (v) => v || "-",
      },
      {
        key: "torreOConjunto",
        dataIndex: "torreOConjunto",
        title: "Torre/Conjunto",
        width: 150,
        ellipsis: true,
        sorter: (a, b) =>
          (a.torreOConjunto ?? "").localeCompare(b.torreOConjunto ?? ""),
        render: (v) => v || "-",
      },
      {
        key: "piso",
        dataIndex: "piso",
        title: "Piso",
        width: 80,
        align: "center",
        sorter: (a, b) => (a.piso ?? 0) - (b.piso ?? 0),
        render: (v) => v ?? "-",
      },
      {
        key: "numeroDeptoCasa",
        dataIndex: "numeroDeptoCasa",
        title: "N° Depto/Casa",
        width: 120,
        sorter: (a, b) =>
          (a.numeroDeptoCasa ?? "").localeCompare(b.numeroDeptoCasa ?? ""),
        render: (v) => v || "-",
      },
      {
        key: "nombreCondominio",
        dataIndex: "nombreCondominio",
        title: "Condominio",
        width: 200,
        ellipsis: true,
        sorter: (a, b) =>
          (a.nombreCondominio ?? "").localeCompare(b.nombreCondominio ?? ""),
        render: (v) => v || "-",
      },
      {
        key: "estacionamiento",
        dataIndex: "estacionamiento",
        title: "Estacionamiento",
        width: 150,
        ellipsis: true,
        sorter: (a, b) =>
          (a.estacionamiento ?? "").localeCompare(b.estacionamiento ?? ""),
        render: (v) => v || "-",
      },

      {
        key: "otros",
        dataIndex: "otros",
        title: "Otros",
        width: 260,
        ellipsis: true,
        render: (v) => v || "-",
      },

      {
        key: "claveWifi",
        dataIndex: "claveWifi",
        title: "WiFi",
        width: 180,
        ellipsis: true,
        render: (v) => v || "-",
      },
      {
        key: "claveCerradura",
        dataIndex: "claveCerradura",
        title: "Clave cerradura",
        width: 200,
        ellipsis: true,
        render: (v) => v || "-",
      },

      {
        key: "lavanderiaId",
        dataIndex: "lavanderiaId",
        title: COL_LABELS.lavanderiaId,
        width: 180,
        ellipsis: true,
        sorter: (a, b) =>
          (lavanderiaById.get(a.lavanderiaId!) ?? "").localeCompare(
            lavanderiaById.get(b.lavanderiaId!) ?? "",
          ),
        render: (v: number | null) =>
          renderMappedName(v, lavanderiaById, catalogsLoading),
      },

      {
        key: "acciones",
        title: "Acciones",
        width: 160,
        fixed: isMobile ? undefined : "right",
        align: "center",
        render: (_: any, r: Propiedad) => (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(r)}
            >
              Editar
            </Button>
            {/* Round 2026-08-06 — antes esto era un Popconfirm que enumeraba de
                memoria lo que se iba a borrar. Ahora abre un modal que lo
                CONSULTA (`/impacto-borrado`): muestra las cifras reales, obliga
                a revisar el inventario y el historial de compras asociados, y
                permite ver la línea de tiempo de cada producto antes de
                decidir. Ver EliminarPropiedadModal. */}
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                setBorrarTarget({ id: r.id, nombre: r.nombrePropiedad })
              }
            >
              Eliminar
            </Button>
          </Space>
        ),
      },
    ];

    return cols;
  }, [
    toggleHabilitado,
    form,
    catalogsLoading,
    usersById,
    clienteAvatarById,
    paisById,
    regionById,
    comunaById,
    sectorById,
    lavanderiaById,
    isMobile,
  ]);

  const filteredColumns = useMemo(() => {
    const vis = new Set(visibleCols);
    return allColumns.filter((c: any) => vis.has(c.key as string));
  }, [allColumns, visibleCols]);

  // columnSelector — extraído a PropiedadesColumnSelector. Round 2026-04-27 (Fase 8).
  const columnSelector = (
    <PropiedadesColumnSelector
      labels={COL_LABELS}
      visible={visibleCols as ColKey[]}
      onChange={(next) => setVisibleCols(next)}
      defaultVisible={defaultVisible as ColKey[]}
      alwaysOn={"acciones"}
    />
  );

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div className="propiedades-container">
        <PageHeader
          title="Propiedades"
          helpText="Administra propiedades, comisiones, claves de acceso y mensajes iniciales."
        />

        <Tabs
          activeKey={pestana}
          onChange={setPestana}
          items={[
            {
              key: "propiedades",
              label: "Propiedades",
              children: (
                <>
                  <main className="propiedades-main">
                    <div className="propiedades-topbar">
                      <div className="propiedades-filters">
                        <div className="propiedades-filter-field">
                          <Text strong>Propietario / Cliente</Text>
                          {/*
                            Búsqueda en vivo (estilo /clientes-propietarios): se
                            escribe y la tabla se filtra server-side (debounced
                            en el hook). El término matchea nombre/correo del
                            CLIENTE y de los PROPIETARIOS; el toggle de abajo
                            acota por tipo. Antes era un <Select mode="tags">
                            que permitía crear etiquetas de texto libre y dejaba
                            "fantasmas" de selecciones previas (de ahí matches
                            que parecían bugs). Ver property.controller.js
                            getAllProperties para el match server-side.
                          */}
                          <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            style={{ width: 280 }}
                            placeholder="Buscar por nombre o correo…"
                            value={ownerSearch}
                            onChange={(e) => setOwnerSearch(e.target.value)}
                          />
                          <Segmented
                            size="small"
                            style={{ marginTop: 6 }}
                            value={ownerType}
                            onChange={(v) =>
                              setOwnerType(
                                v as "both" | "cliente" | "propietario",
                              )
                            }
                            options={[
                              { label: "Ambos", value: "both" },
                              { label: "Cliente", value: "cliente" },
                              { label: "Propietario", value: "propietario" },
                            ]}
                          />
                        </div>

                        <div className="propiedades-filter-field">
                          <Text strong>Propiedad / Publicación</Text>
                          <Select
                            mode="tags"
                            showSearch
                            style={{ width: 320 }}
                            placeholder="Buscar o seleccionar"
                            value={qProp}
                            onChange={(v) => setQProp(v)}
                            maxTagCount={2}
                            maxTagPlaceholder={(omitted) =>
                              `+${omitted.length}`
                            }
                            filterOption={(input, option) =>
                              fuzzyIncludes(option?.label, input)
                            }
                            options={[...allPropertyNames]
                              .sort((a, b) => {
                                const aSelected = qProp.includes(
                                  a.nombrepropiedad,
                                );
                                const bSelected = qProp.includes(
                                  b.nombrepropiedad,
                                );
                                if (aSelected && !bSelected) return -1;
                                if (!aSelected && bSelected) return 1;
                                return 0;
                              })
                              .map((p) => ({
                                value: p.nombrepropiedad,
                                label: formatPropertyName(p.nombrepropiedad),
                              }))}
                            allowClear
                          />
                        </div>

                        {/*
                          Envuelto en un field con un label-spacer OCULTO para
                          que el botón quede alineado con los rectángulos de
                          búsqueda (la fila de inputs), no con la fila de labels.
                          El toggle de tipo del primer campo cuelga por debajo
                          sin descuadrar esta fila (ver .propiedades-filters).
                        */}
                        <div className="propiedades-filter-field">
                          <Text strong style={{ visibility: "hidden" }}>
                            &nbsp;
                          </Text>
                          <Button
                            icon={<FilterOutlined />}
                            onClick={() => setShowAdvanced((s) => !s)}
                          >
                            Filtros avanzados
                          </Button>
                        </div>
                      </div>

                      <div className="propiedades-actions">
                        <Space>
                          <Dropdown
                            trigger={["click"]}
                            dropdownRender={() => columnSelector}
                          >
                            <Button icon={<SettingOutlined />}>Columnas</Button>
                          </Dropdown>
                        </Space>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={openCreate}
                        >
                          Nueva propiedad
                        </Button>
                      </div>
                    </div>

                    {showAdvanced && (
                      <div className="propiedades-advanced">
                        <div className="propiedades-advanced-grid">
                          <div>
                            <Select
                              mode="multiple"
                              style={{ width: 220 }}
                              placeholder="Selecciona países"
                              value={paisIds}
                              onChange={(v) => setPaisIds(v)}
                              options={paisSelectOptions}
                              // fuzzyIncludes pliega tildes/ñ: "nunoa"
                              // encuentra "Ñuñoa" (optionFilterProp era
                              // accent-sensitive). Aud. 2026-07-13.
                              filterOption={(input, option) =>
                                fuzzyIncludes(option?.label, input)
                              }
                              showSearch
                              allowClear
                            />
                          </div>

                          <div>
                            <Select
                              mode="multiple"
                              style={{ width: 220 }}
                              placeholder="Selecciona regiones"
                              value={regionIds}
                              onChange={(v) => setRegionIds(v)}
                              options={filteredRegionOptions
                                .filter((r) => paisIds.length === 0 || paisIds.includes(r.pais))
                                .map((r) => ({
                                  value: r.id,
                                  label: r.region,
                                }))}
                              filterOption={(input, option) =>
                                fuzzyIncludes(option?.label, input)
                              }
                              showSearch
                              allowClear
                            />
                          </div>

                          <div>
                            <Select
                              mode="multiple"
                              style={{ width: 240 }}
                              placeholder="Selecciona comunas"
                              value={comunaIds}
                              onChange={(v) => setComunaIds(v)}
                              options={filteredComunaOptions
                                .filter((c) => {
                                  if (regionIds.length > 0) return regionIds.includes(c.region);
                                  if (paisIds.length > 0) {
                                    const regionIdsForPais = regionOptions.filter((r) => paisIds.includes(r.pais)).map((r) => r.id);
                                    return regionIdsForPais.includes(c.region);
                                  }
                                  return true;
                                })
                                .map((c) => ({
                                  value: c.id,
                                  label: c.comuna,
                                }))}
                              filterOption={(input, option) =>
                                fuzzyIncludes(option?.label, input)
                              }
                              showSearch
                              allowClear
                            />
                          </div>

                          <div>
                            <Select
                              mode="multiple"
                              style={{ width: 220 }}
                              placeholder="Selecciona sectores"
                              value={sectorIds}
                              onChange={(v) => setSectorIds(v)}
                              options={sectorOptions
                                .filter((s) => {
                                  if (comunaIds.length > 0) return comunaIds.includes(s.comuna);
                                  if (regionIds.length > 0) {
                                    const comunaIdsForRegion = comunaOptions.filter((c) => regionIds.includes(c.region)).map((c) => c.id);
                                    return comunaIdsForRegion.includes(s.comuna);
                                  }
                                  if (paisIds.length > 0) {
                                    const regionIdsForPais = regionOptions.filter((r) => paisIds.includes(r.pais)).map((r) => r.id);
                                    const comunaIdsForPais = comunaOptions.filter((c) => regionIdsForPais.includes(c.region)).map((c) => c.id);
                                    return comunaIdsForPais.includes(s.comuna);
                                  }
                                  return true;
                                })
                                .map((s) => ({
                                  value: s.id,
                                  label: s.sector,
                                }))}
                              filterOption={(input, option) =>
                                fuzzyIncludes(option?.label, input)
                              }
                              showSearch
                              allowClear
                            />
                          </div>

                          <div className="propiedades-advanced-actions">
                            <Button onClick={clearGeoFilters}>
                              Limpiar zona
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {error && (
                      <Alert
                        type="error"
                        showIcon
                        message="Error"
                        description={error}
                        style={{ marginBottom: 12 }}
                      />
                    )}

                    <div className="table-card">
                      <Spin spinning={loading} tip="Cargando propiedades...">
                        <Table<Propiedad>
                          rowKey="id"
                          loading={loading}
                          columns={filteredColumns}
                          dataSource={items}
                          scroll={{ x: "max-content" }}
                          pagination={{
                            current: page,
                            pageSize: limit,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            // onChange no se dispara en AntD v5 cuando solo
                            // cambia el pageSize estando en la misma page;
                            // necesitamos onShowSizeChange explícito. Sin él,
                            // el dropdown "20 / página" quedaba muerto.
                            onChange: (p) => setPage(p),
                            onShowSizeChange: (_, size) => {
                              setLimit(size);
                              setPage(1);
                            },
                            showTotal: (t) => `${t} propiedades`,
                          }}
                        />
                      </Spin>
                    </div>
                  </main>

                  <Modal
                    open={openModal}
                    onCancel={closeModal}
                    onOk={onSubmit}
                    okText={editing ? "Guardar cambios" : "Crear"}
                    cancelText="Cancelar"
                    title={editing ? "Editar propiedad" : "Nueva propiedad"}
                    width={1100}
                  >
                    <Form form={form} layout="vertical">
                      {/*
                       * Modal agrupado por PROPÓSITO semántico. Los 3 paneles
                       * de arriba describen lo que la propiedad ES — identidad,
                       * dueños, ubicación — y quedan abiertos por default. Los
                       * 3 de abajo son CONFIGURACIÓN (finanzas, PMS, info al
                       * huésped) y quedan cerrados para no saturar la primera
                       * pantalla.
                       *
                       * forceRender en cada panel mantiene los Form.Items
                       * montados: los valores persisten aunque el panel esté
                       * cerrado y la autocalculación del Desglose de limpieza
                       * sigue corriendo siempre.
                       */}
                      <Collapse
                        defaultActiveKey={["identity", "ownership", "location"]}
                        ghost
                        items={[
                          {
                            key: "identity",
                            forceRender: true,
                            label: <Text strong>Identificación</Text>,
                            children: (
                              <PropiedadIdentityPanel
                                existingNames={allPropertyNames}
                                editingId={editing?.id ?? null}
                                editingOriginalName={
                                  editing?.nombrePropiedad ?? null
                                }
                              />
                            ),
                          },
                          {
                            key: "ownership",
                            forceRender: true,
                            label: <Text strong>Propietario y Cliente</Text>,
                            children: (
                              <PropiedadOwnershipPanel
                                form={form}
                                users={users}
                                editing={editing}
                                onCreateOwner={() => {
                                  setUserCreateTarget("owner");
                                  setOpenUserModal(true);
                                }}
                                onCreateClient={() => {
                                  setUserCreateTarget("client");
                                  setOpenUserModal(true);
                                }}
                              />
                            ),
                          },
                          {
                            key: "location",
                            forceRender: true,
                            label: <Text strong>Ubicación</Text>,
                            children: (
                              <PropiedadLocationPanel
                                form={form}
                                paisSelectOptions={paisSelectOptions}
                                regionSelectOptions={regionSelectOptions}
                                comunaSelectOptions={comunaSelectOptions}
                                sectorSelectOptions={sectorSelectOptions}
                                watchPaisModal={watchPaisModal}
                                watchRegionModal={watchRegionModal}
                                watchComunaModal={watchComunaModal}
                                comunaById={comunaById}
                                sectorOptions={sectorOptions}
                                setSectorOptions={setSectorOptions}
                                creandoSector={creandoSector}
                                setCreandoSector={setCreandoSector}
                                sectorSelectRef={sectorSelectRef}
                              />
                            ),
                          },
                          {
                            key: "finance",
                            forceRender: true,
                            label: (
                              <Text strong>
                                Configuración financiera{" "}
                                <Text type="secondary" style={{ fontWeight: "normal" }}>
                                  — plan, comisión AF, desglose limpieza
                                </Text>
                              </Text>
                            ),
                            children: (
                              <PropiedadFinancePanel
                                form={form}
                                planOptions={planOptions}
                                setPlanOptions={setPlanOptions}
                                planSelectRef={planSelectRef}
                                lavanderiaOptions={lavanderiaOptions}
                                handleNetoChange={handleNetoChange}
                                handleTotalChange={handleTotalChange}
                                deltaTransporte={deltaTransporte}
                                deltaMateriales={deltaMateriales}
                                deltaLavanderia={deltaLavanderia}
                                sumaSubitems={sumaSubitems}
                                hasDeltaSubitems={hasDeltaSubitems}
                              />
                            ),
                          },
                          {
                            key: "integration",
                            forceRender: true,
                            label: (
                              <Text strong>
                                Integración PMS{" "}
                                <Text type="secondary" style={{ fontWeight: "normal" }}>
                                  — Stays, Airbnb…
                                </Text>
                              </Text>
                            ),
                            children: (
                              <PropiedadIntegrationPanel
                                form={form}
                                buscandoIdLargo={buscandoIdLargo}
                                setBuscandoIdLargo={setBuscandoIdLargo}
                                idLargoUnlocked={idLargoUnlocked}
                                setIdLargoUnlocked={setIdLargoUnlocked}
                                apiBaseUrl={API_BASE_URL}
                              />
                            ),
                          },
                          {
                            key: "guest",
                            forceRender: true,
                            label: (
                              <Text strong>
                                Información para el huésped{" "}
                                <Text type="secondary" style={{ fontWeight: "normal" }}>
                                  — mensaje, WiFi, cerradura, archivos
                                </Text>
                              </Text>
                            ),
                            children: (
                              <PropiedadGuestPanel
                                propiedadId={editing?.id ?? null}
                                modalOpen={openModal}
                              />
                            ),
                          },
                        ]}
                      />
                    </Form>
                  </Modal>

                  {/*
                    Modal extraído a CreateUserModal — round 2026-04-27
                    (Fase 8). Maneja su propio Form interno; al crear OK
                    avisa vía onCreated para que enchufemos el id nuevo
                    al campo correcto del form de propiedad.
                  */}
                  <CreateUserModal
                    open={openUserModal}
                    target={userCreateTarget}
                    createUsuario={createUsuario}
                    onCancel={() => setOpenUserModal(false)}
                    onCreated={(target, newId) => {
                      if (target === "owner") {
                        const current =
                          form.getFieldValue("propietarioIds") ?? [];
                        form.setFieldValue("propietarioIds", [
                          ...current,
                          newId,
                        ]);
                      } else {
                        form.setFieldValue("idCliente", newId);
                      }
                    }}
                  />
                </>
              ),
            },
            {
              key: "bodeguitas",
              label: "Bodeguitas",
              children: <BodeguitasTab />,
            },
            {
              key: "bodegas",
              label: "Bodegas",
              children: <BodegasTab />,
            },
          ]}
        />

        {/* Borrado con revisión obligatoria del inventario asociado. Vive fuera
            de la tabla para que el Drawer de historia que abre por dentro no
            quede anidado dentro de una fila. */}
        <EliminarPropiedadModal
          propiedadId={borrarTarget?.id ?? null}
          nombrePropiedad={borrarTarget?.nombre ?? ""}
          open={borrarTarget != null}
          onClose={() => setBorrarTarget(null)}
          onConfirmar={async (confirmacion) => {
            if (!borrarTarget) return false;
            return await deletePropiedad(borrarTarget.id, confirmacion);
          }}
        />
      </div>
    </Layout>
  );
}
